<template>
  <div class="experimental-game-creator">
    <v-app-bar app color="primary" dark>
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title>Experimental Game Creator</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip
        :color="validationStatus.isValid ? 'success' : 'warning'"
        text-color="white"
        class="mr-2"
      >
        {{ completionStatus.percentage }}% Complete
      </v-chip>
      <v-btn icon @click="exportBlueprint">
        <v-icon>mdi-download</v-icon>
      </v-btn>
      <v-btn icon @click="showPreview = true">
        <v-icon>mdi-eye</v-icon>
      </v-btn>
      <v-btn icon @click="toggleConversation" :color="showConversation ? 'primary' : ''">
        <v-icon>mdi-robot</v-icon>
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" app>
      <v-list>
        <v-list-item>
          <v-list-item-title class="title">
            Blueprint Sections
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="activeSection = 'meta'">
          <v-list-item-icon>
            <v-icon>mdi-information</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Meta</v-list-item-title>
        </v-list-item>
        <v-list-item @click="activeSection = 'objectives'">
          <v-list-item-icon>
            <v-icon>mdi-target</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Objectives</v-list-item-title>
        </v-list-item>
        <v-list-item @click="activeSection = 'controls'">
          <v-list-item-icon>
            <v-icon>mdi-gamepad</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Controls</v-list-item-title>
        </v-list-item>
        <v-list-item @click="activeSection = 'rules'">
          <v-list-item-icon>
            <v-icon>mdi-cog</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Rules</v-list-item-title>
        </v-list-item>
        <v-list-item @click="activeSection = 'levels'">
          <v-list-item-icon>
            <v-icon>mdi-map</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Levels</v-list-item-title>
        </v-list-item>
        <v-list-item @click="activeSection = 'assets'">
          <v-list-item-icon>
            <v-icon>mdi-image</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Assets</v-list-item-title>
        </v-list-item>
        <v-list-item @click="activeSection = 'progression'">
          <v-list-item-icon>
            <v-icon>mdi-arrow-right</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Progression</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <div class="blueprint-editor">
        <!-- Meta Section -->
        <div v-if="activeSection === 'meta'" class="section-card">
          <v-card class="pa-4">
            <v-card-title>Game Meta</v-card-title>
            <v-text-field v-model="blueprint.meta.title" label="Game Title" outlined></v-text-field>
            <v-text-field v-model="blueprint.meta.author" label="Author" outlined></v-text-field>
            <v-select
              v-model="blueprint.meta.kernel"
              :items="kernelOptions"
              label="Game Kernel"
              outlined
            ></v-select>
            <v-select
              v-model="blueprint.meta.mode"
              :items="modeOptions"
              label="Mode"
              outlined
            ></v-select>
          </v-card>
        </div>

        <!-- Objectives Section -->
        <div v-if="activeSection === 'objectives'" class="section-card">
          <v-card class="pa-4">
            <v-card-title>Objectives</v-card-title>
            <div v-for="(objective, index) in blueprint.objectives" :key="index" class="mb-4">
              <v-text-field
                v-model="blueprint.objectives[index]"
                :label="`Objective ${index + 1}`"
                outlined
              ></v-text-field>
              <v-btn icon small @click="removeObjective(index)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
            <v-btn @click="addObjective" color="primary">
              <v-icon left>mdi-plus</v-icon>
              Add Objective
            </v-btn>
          </v-card>
        </div>

        <!-- Controls Section -->
        <div v-if="activeSection === 'controls'" class="section-card">
          <v-card class="pa-4">
            <v-card-title>Controls</v-card-title>
            <v-select
              v-model="blueprint.controls.scheme"
              :items="controlSchemeOptions"
              label="Control Scheme"
              outlined
            ></v-select>
            <div v-if="blueprint.controls.scheme === 'keyboard'" class="mt-4">
              <v-text-field v-model="blueprint.controls.map.left" label="Left Key" outlined dense></v-text-field>
              <v-text-field v-model="blueprint.controls.map.right" label="Right Key" outlined dense></v-text-field>
              <v-text-field v-model="blueprint.controls.map.jump" label="Jump Key" outlined dense></v-text-field>
            </div>
          </v-card>
        </div>

        <!-- Rules Section -->
        <div v-if="activeSection === 'rules'" class="section-card">
          <v-card class="pa-4">
            <v-card-title>Game Rules</v-card-title>
            <div v-for="(rule, index) in blueprint.rules" :key="index" class="rule-card mb-4">
              <v-card outlined class="pa-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="subtitle-2">Rule {{ index + 1 }}</span>
                  <v-btn icon small @click="removeRule(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <v-select
                  v-model="rule.when"
                  :items="ruleTriggerOptions"
                  label="When"
                  outlined
                  dense
                ></v-select>
                <v-text-field
                  v-if="rule.when === 'input' || rule.when === 'overlap' || rule.when === 'reach'"
                  v-model="rule.a"
                  label="Actor A"
                  outlined
                  dense
                ></v-text-field>
                <v-text-field
                  v-if="rule.when !== 'timer'"
                  v-model="rule.b"
                  label="Actor B / Target"
                  outlined
                  dense
                ></v-text-field>
                <v-text-field
                  v-model="rule.do"
                  label="Actions"
                  outlined
                  dense
                  placeholder="e.g., score+1, destroy(b), win"
                ></v-text-field>
              </v-card>
            </div>
            <v-btn @click="addRule" color="primary">
              <v-icon left>mdi-plus</v-icon>
              Add Rule
            </v-btn>
          </v-card>
        </div>

        <!-- Levels Section -->
        <div v-if="activeSection === 'levels'" class="section-card">
          <v-card class="pa-4">
            <v-card-title>Levels</v-card-title>
            <div v-for="(level, index) in blueprint.levels" :key="index" class="level-card mb-4">
              <v-card outlined class="pa-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="subtitle-2">{{ level.id }}</span>
                  <v-btn icon small @click="removeLevel(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <v-text-field v-model="level.theme" label="Theme" outlined dense></v-text-field>
                <v-text-field v-model="level.goal" label="Level Goal" outlined dense></v-text-field>
                <v-textarea
                  v-model="level.entities"
                  label="Entities (JSON)"
                  outlined
                  dense
                  placeholder='[{"id": "gem1", "prefab": "Gem", "x": 100, "y": 100}]'
                ></v-textarea>
              </v-card>
            </div>
            <v-btn @click="addLevel" color="primary">
              <v-icon left>mdi-plus</v-icon>
              Add Level
            </v-btn>
          </v-card>
        </div>

        <!-- Assets Section -->
        <div v-if="activeSection === 'assets'" class="section-card">
          <v-card class="pa-4">
            <v-card-title>Assets</v-card-title>
            <v-select
              v-model="blueprint.assets.packs"
              :items="availableAssetPacks"
              label="Asset Packs"
              multiple
              outlined
              chips
              item-text="text"
              item-value="value"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
            <v-textarea
              v-model="blueprint.assets.overrides"
              label="Asset Overrides (JSON)"
              outlined
              dense
              placeholder='{"player_sprite": "custom/dragon.png"}'
            ></v-textarea>
          </v-card>
        </div>

        <!-- Progression Section -->
        <div v-if="activeSection === 'progression'" class="section-card">
          <v-card class="pa-4">
            <v-card-title>Progression</v-card-title>
            <div v-for="(edge, index) in blueprint.progression" :key="index" class="progression-card mb-4">
              <v-card outlined class="pa-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="subtitle-2">Progression {{ index + 1 }}</span>
                  <v-btn icon small @click="removeProgression(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <v-text-field v-model="edge.from" label="From Level" outlined dense></v-text-field>
                <v-text-field v-model="edge.to" label="To Level" outlined dense></v-text-field>
                <v-text-field v-model="edge.condition" label="Condition" outlined dense></v-text-field>
              </v-card>
            </div>
            <v-btn @click="addProgression" color="primary">
              <v-icon left>mdi-plus</v-icon>
              Add Progression
            </v-btn>
          </v-card>
        </div>
      </div>
    </v-main>

    <!-- Conversation Panel -->
    <v-navigation-drawer
      v-model="showConversation"
      app
      right
      width="400"
      temporary
    >
      <ConversationPanel
        :blueprint="blueprint"
        height="100%"
        @blueprint-updated="handleBlueprintUpdate"
        @close="showConversation = false"
      />
    </v-navigation-drawer>

    <!-- Blueprint Preview Dialog -->
    <v-dialog v-model="showPreview" max-width="800px">
      <v-card>
        <v-card-title>Blueprint Preview</v-card-title>
        <v-card-text>
          <pre>{{ JSON.stringify(blueprint, null, 2) }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="showPreview = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  validateBlueprint,
  createDefaultBlueprint,
  calculateCompletionScore,
  KernelDefinitions,
  AssetPacks,
  exportBlueprintAsJSON
} from '@/types/blueprint'
import ConversationPanel from '@/components/experimental/ConversationPanel.vue'

export default {
  name: 'ExperimentalGameCreator',

  components: {
    ConversationPanel,
  },

  data() {
    return {
      drawer: true,
      activeSection: 'meta',
      showPreview: false,
      showConversation: false,
      blueprint: createDefaultBlueprint(),

      // Options for dropdowns
      kernelOptions: [
        { text: 'Platformer', value: 'platformer' },
        { text: 'Top-Down', value: 'topdown' },
        { text: 'Card Battler', value: 'card' },
        { text: 'Puzzle', value: 'puzzle' },
        { text: 'Tactics', value: 'tactics' }
      ],
      modeOptions: [
        { text: 'Prototype', value: 'prototype' },
        { text: 'Full Game', value: 'full' }
      ],
      controlSchemeOptions: [
        { text: 'Keyboard', value: 'keyboard' },
        { text: 'Touch', value: 'touch' }
      ],
      ruleTriggerOptions: [
        { text: 'Overlap', value: 'overlap' },
        { text: 'Reach', value: 'reach' },
        { text: 'Timer', value: 'timer' },
        { text: 'Input', value: 'input' },
        { text: 'Defeat', value: 'defeat' },
        { text: 'Collect', value: 'collect' }
      ],
      assetPackOptions: [] // Will be populated by computed property
    }
  },

  methods: {
    addObjective() {
      this.blueprint.objectives.push('')
    },
    removeObjective(index) {
      this.blueprint.objectives.splice(index, 1)
    },
    addRule() {
      this.blueprint.rules.push({
        id: `R${this.blueprint.rules.length + 1}`,
        when: 'overlap',
        a: 'player',
        b: '',
        do: ''
      })
    },
    removeRule(index) {
      this.blueprint.rules.splice(index, 1)
    },
    addLevel() {
      this.blueprint.levels.push({
        id: `level_${this.blueprint.levels.length + 1}`,
        theme: '',
        goal: '',
        entities: []
      })
    },
    removeLevel(index) {
      this.blueprint.levels.splice(index, 1)
    },
    addProgression() {
      this.blueprint.progression.push({
        from: '',
        to: '',
        condition: ''
      })
    },
    removeProgression(index) {
      this.blueprint.progression.splice(index, 1)
    },
    exportBlueprint() {
      const dataStr = exportBlueprintAsJSON(this.blueprint)
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)

      const exportFileDefaultName = `${this.blueprint.meta.title.toLowerCase().replace(/\s+/g, '_')}_blueprint.json`

      const linkElement = document.createElement('a')
      linkElement.setAttribute('href', dataUri)
      linkElement.setAttribute('download', exportFileDefaultName)
      linkElement.click()
    },

    toggleConversation() {
      this.showConversation = !this.showConversation
    },

    handleBlueprintUpdate(updatedBlueprint) {
      this.blueprint = { ...updatedBlueprint }
      // Force reactivity update
      this.$forceUpdate()

      // Show a brief success message
      this.$nextTick(() => {
        // Could add a snackbar notification here
        console.log('Blueprint updated via conversation')
      })
    }
  },

  computed: {
    completionStatus() {
      return calculateCompletionScore(this.blueprint)
    },

    validationStatus() {
      return validateBlueprint(this.blueprint)
    },

    currentKernel() {
      return KernelDefinitions[this.blueprint.meta.kernel] || KernelDefinitions.platformer
    },

    availableAssetPacks() {
      return Object.keys(AssetPacks).map(key => ({
        text: AssetPacks[key].name,
        value: key,
        description: AssetPacks[key].description
      }))
    }
  },
}
</script>

<style lang="scss" scoped>
.experimental-game-creator {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.blueprint-editor {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.section-card {
  max-width: 800px;
  margin: 0 auto 24px auto;
}

.rule-card, .level-card, .progression-card {
  position: relative;
}

.v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
}

pre {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.4;
}
</style>
